import { useState, useCallback } from 'react';
import { useAuth } from 'react-oidc-context';
import FlightRangeService from '../services/FlightRangeService';
import IFlightRangeData from '../types/FlightRanges';

/**
 * Custom hook for managing flight range history data
 * Follows Single Responsibility Principle - only handles history data fetching and state
 */
export const useFlightRangeHistory = () => {
  const [historyData, setHistoryData] = useState<IFlightRangeData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const auth = useAuth();

  /**
   * Fetches flight range history and sorts by modification date (latest first)
   * Falls back to current flight ranges if history is unavailable
   * Maintains low cyclomatic complexity with clear error handling
   */
  const fetchHistory = useCallback(async () => {
    // Handle unauthenticated state
    if (!auth.isAuthenticated || !auth.user?.access_token) {
      setIsLoading(false);
      setError('You must be authenticated to view flight range history.');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const token = auth.user.access_token;

      // Try the history endpoint first (includes pdfBlobName)
      try {
        const response = await FlightRangeService.getFlightRangeHistory(token);

        // If history is available and not empty, use it
        if (response.data && response.data.length > 0) {
          const sortedData = [...response.data].sort((a, b) => {
            const dateA = new Date(a.modDate).getTime();
            const dateB = new Date(b.modDate).getTime();
            return dateB - dateA; // Latest first
          });
          setHistoryData(sortedData);
          return;
        }
      } catch (historyErr) {
        console.error('History endpoint unavailable, falling back to current flight ranges');
      }

      // Fallback to current flight ranges if history is empty or unavailable
      const fallbackResponse = await FlightRangeService.getFlightRanges(token);

      // Sort by modification date descending (latest first)
      const sortedData = [...fallbackResponse.data].sort((a, b) => {
        const dateA = new Date(a.modDate).getTime();
        const dateB = new Date(b.modDate).getTime();
        return dateB - dateA; // Latest first
      });

      setHistoryData(sortedData);
    } catch (err) {
      const errorMessage = 'Failed to load flight range history. Please try again.';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [auth.isAuthenticated, auth.user?.access_token]);

  return { 
    historyData, 
    isLoading, 
    error, 
    fetchHistory 
  };
};